import React, { useEffect, useRef, useState } from "react"
import { ReactSVG } from "react-svg"
import { graphql, useStaticQuery } from "gatsby"
import { AlertBannerFieldsFragment, DatoCmsInfoTraffic, HomeQuery } from "../../../../graphql-types"
import * as config from "../../../../config"
import * as styles from "./alert-banner.module.scss"
import { format } from "date-fns"
import sanitizeHtml from "sanitize-html"
import { pushGtmEvent } from "../../../gtm/interaction-event"
import { alertImage, arrowDown, cross } from "../../../../static/images"
import { useInternalExternalLink } from "../../../hooks"
import { setInert } from "../../../utils/accessibility"
import { detectWebView } from "../../../utils"

type RenderProps = {
  infoTrafficData: DatoCmsInfoTraffic
  buttonText: HomeQuery["datoCmsGlobalConfiguration"]["redirectionButtonText"]
  alertBannerConfig: AlertBannerFieldsFragment
  defaultLocale: string
}

const AlertBanner: React.FC<RenderProps> = ({
  infoTrafficData,
  buttonText,
  alertBannerConfig: {
    alertBannerIsActive,
    alertBannerTitle,
    alertBannerDate,
    alertBannerInternalExternalLink,
    alertBannerShortDescription,
    alertBannerMoreInfoButtonLabel,
    alertBannerClosingButtonAlt,
  },
  defaultLocale,
}) => {
  const globalConfig = useStaticQuery(graphql`
    query infoTrafficDetailPageQuery {
      allDatoCmsPage(filter: { isInfoTrafficDetailPage: { eq: true } }) {
        nodes {
          id
          title
          slug
          isInfoTrafficDetailPage
        }
      }
    }
  `)

  const [isBannerOpened, setBannerState] = useState(false)
  const bannerRef = useRef<HTMLDivElement>(null)
  // In webview mode the "more info" button is replaced by a down chevron
  const isWebView = detectWebView()

  const closeBanner = () => {
    pushGtmEvent("eventga", "Homepage", "Info Banner", alertBannerClosingButtonAlt)
    setBannerState(false)
    setTimeout(function () {
      if (typeof window !== "undefined") {
        sessionStorage.setItem("id", alertBannerIsActive ? "generalBanner" : infoTrafficData.id)
      }
    }, 1000)
  }

  let infoTrafficId: string
  let generalBanner: string

  useEffect(() => {
    if (typeof window !== "undefined") {
      infoTrafficId = sessionStorage.getItem("id")
      generalBanner = sessionStorage.getItem("id")
    }
    if (
      (!infoTrafficId || infoTrafficId == "undefined" || infoTrafficId == "" || infoTrafficId == undefined) &&
      (!generalBanner || generalBanner != "generalBanner")
    ) {
      setBannerState(true)
    }
  }, [])

  useEffect(() => {
    setInert(bannerRef.current, !isBannerOpened)
  }, [isBannerOpened])

  // Error displayed by date-format => RangeError: Use `yyyy` instead of `YYYY` (in `dd/MM/YYYY`) for formatting years to the input
  const dateFormat =
    config.date_format[config.locale] === "dd/MM/YYYY" ? "dd/MM/yyyy" : config.date_format[config.locale]

  const formatBannerDate = (value: string | Date) => {
    const date = new Date(value)
    return {
      dateTime: date.toISOString(),
      label: format(date, dateFormat),
    }
  }

  const link = alertBannerInternalExternalLink[0]
    ? useInternalExternalLink(alertBannerInternalExternalLink[0], config.locale, defaultLocale)
    : null
  const cmsAlertBannerDate = formatBannerDate(alertBannerDate)
  const infoTrafficBannerDate = infoTrafficData ? formatBannerDate(infoTrafficData.start) : null

  const closeButton = (
    <div className={styles.closeButtonWrapper}>
      <button
        type="button"
        className={styles.closeButton}
        onClick={closeBanner}
        aria-label={alertBannerClosingButtonAlt}
      >
        <img src={cross} alt="" aria-hidden="true" />
      </button>
    </div>
  )

  const bannerClassName = `${styles.alertBanner}${isBannerOpened ? "" : ` ${styles.alertBannerClosed}`}`

  return alertBannerIsActive && !generalBanner ? (
    <div ref={bannerRef} className={bannerClassName}>
      <div className={styles.alertBannerContentWrapper}>
        <ReactSVG
          src={alertImage}
          className={styles.images}
          beforeInjection={svg => svg.setAttribute("aria-hidden", "true")}
        />
        <div className={styles.alertBannerBody}>
          <h2 className={styles.title}>{alertBannerTitle}</h2>
          <time className={styles.infoTrafficDate} dateTime={cmsAlertBannerDate.dateTime}>
            {cmsAlertBannerDate.label}
          </time>
          <div
            className={styles.shortDescription}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(alertBannerShortDescription, {
                allowedTags: ["p"],
              }),
            }}
          />
        </div>
        {link && (
          <div className={styles.alertBannerButton}>
            <a
              href={link}
              className={isWebView ? styles.alertBannerChevron : styles.alertBannerLink}
              target={alertBannerInternalExternalLink[0]?.externalLink ? "_blank" : "_self"}
              onClick={() =>
                pushGtmEvent("eventga", "Homepage", "Info Banner", link ? alertBannerMoreInfoButtonLabel : buttonText)
              }
              rel="noreferrer"
            >
              {isWebView ? (
                <ReactSVG
                  src={arrowDown}
                  className={styles.chevronIcon}
                  beforeInjection={svg => svg.setAttribute("aria-hidden", "true")}
                />
              ) : (
                alertBannerMoreInfoButtonLabel || buttonText
              )}
            </a>
          </div>
        )}
        {closeButton}
      </div>
    </div>
  ) : (
    infoTrafficId !== infoTrafficData?.id && (
      <div ref={bannerRef} className={bannerClassName}>
        <div className={styles.alertBannerContentWrapper}>
          <ReactSVG
            src={alertImage}
            className={styles.images}
            beforeInjection={svg => svg.setAttribute("aria-hidden", "true")}
          />
          <div className={styles.alertBannerBody}>
            <h2 className={styles.title}>{infoTrafficData.title}</h2>
            {infoTrafficBannerDate && (
              <time className={styles.infoTrafficDate} dateTime={infoTrafficBannerDate.dateTime}>
                {infoTrafficBannerDate.label}
              </time>
            )}
            <div
              className={styles.shortDescription}
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(infoTrafficData.shortDescription, {
                  allowedTags: ["p"],
                }),
              }}
            />
          </div>
          <div className={styles.alertBannerButton}>
            <a
              href={
                !globalConfig?.allDatoCmsPage?.nodes[0]?.slug
                  ? `/traffic-info/${infoTrafficData.slug}`
                  : `/${
                      globalConfig?.allDatoCmsPage?.nodes[0]?.slug ? globalConfig.allDatoCmsPage.nodes[0].slug : ""
                    }` +
                    "?" +
                    infoTrafficData.title.replace(new RegExp(" ", "g"), "-") +
                    "#" +
                    infoTrafficData.id
              }
              className={isWebView ? styles.alertBannerChevron : styles.alertBannerLink}
              onClick={() => pushGtmEvent("eventga", "Homepage", "Info Banner", buttonText)}
            >
              {isWebView ? (
                <ReactSVG
                  src={arrowDown}
                  className={styles.chevronIcon}
                  beforeInjection={svg => svg.setAttribute("aria-hidden", "true")}
                />
              ) : (
                buttonText
              )}
            </a>
          </div>
          {closeButton}
        </div>
      </div>
    )
  )
}

export const fragment = graphql`
  fragment AlertBannerFields on DatoCmsHome {
    alertBannerIsActive
    alertBannerTitle
    alertBannerDate
    alertBannerInternalExternalLink {
      ...InternalExternalLinkFields
    }
    alertBannerShortDescription
    alertBannerMoreInfoButtonLabel
    alertBannerClosingButtonAlt
  }
`

export default AlertBanner
